"use client";

import Link from "next/link";
import { useParams } from "next/navigation";
import { motion } from "framer-motion";
import {
  fadeScale,
  fadeUp,
  stagger,
  cardItem,
  viewportOnce,
} from "../../../components/motionVariants";
import CtaBanner from "../../../components/CtaBanner";
import { getPost, getRelated } from "../blogContent";

const sectionPadding = "px-4 sm:px-6 md:px-10 lg:px-12";

// ─── Block renderer ────────────────────────────────────────────────────────
function Block({ block }) {
  switch (block.type) {
    case "heading":
      return (
        <motion.h2
          variants={fadeUp}
          className="text-[22px] sm:text-[28px] md:text-[32px] leading-[1.2] text-[#171717] sentient-medium tracking-tight pt-2"
        >
          {block.text}
        </motion.h2>
      );

    case "paragraph":
      return (
        <motion.p
          variants={fadeUp}
          className="text-[#333333] text-[14px] sm:text-[15px] md:text-[16px] leading-[1.85] geist-medium"
        >
          {block.text}
        </motion.p>
      );

    case "list":
      return (
        <motion.ul variants={fadeUp} className="space-y-2 pl-1">
          {block.items.map((item, i) => (
            <li
              key={i}
              className="flex items-start gap-2 geist-medium text-sm text-[#525252]"
            >
              <span className="text-[#DF5D01] flex-shrink-0 mt-0.5">•</span>
              {item}
            </li>
          ))}
        </motion.ul>
      );

    case "tldr":
      return (
        <motion.div
          variants={fadeUp}
          className="border-l-4 border-[#DF5D01] bg-[#FFF7ED] rounded-r-xl p-6"
        >
          <p className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-semibold uppercase tracking-wider mb-3">
            TL;DR
          </p>
          <ul className="space-y-2">
            {block.items.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-2 geist-medium text-sm text-[#525252]"
              >
                <span className="text-[#DF5D01] flex-shrink-0 mt-0.5">✓</span>
                {item}
              </li>
            ))}
          </ul>
        </motion.div>
      );

    case "callout":
      return (
        <motion.div
          variants={fadeUp}
          className="border border-[#e5e5e5] bg-[#fafafa] rounded-xl p-6"
        >
          <p className="geist-semibold text-sm text-[#171717] mb-2 tracking-tight">
            {block.title}
          </p>
          <p className="geist-medium text-sm text-[#525252] leading-relaxed">
            {block.text}
          </p>
        </motion.div>
      );

    case "table":
      return (
        <motion.div
          variants={fadeUp}
          className="overflow-x-auto rounded-xl border border-[#e5e5e5] shadow-sm"
        >
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-[#171717] text-white">
                {block.headers.map((h, i) => (
                  <th key={i} className="text-left geist-semibold px-5 py-3">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr
                  key={i}
                  className={`border-b border-[#e5e5e5] ${i % 2 === 0 ? "bg-white" : "bg-[#fafafa]"}`}
                >
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      className={`px-5 py-3 ${j === 0 ? "geist-medium text-[#525252]" : "geist-semibold text-[#171717]"}`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      );

    case "steps":
      return (
        <motion.div variants={stagger} className="space-y-4">
          {block.items.map((item, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              className="flex gap-5 border border-[#e5e5e5] rounded-xl p-5 sm:p-6 bg-[#fafafa] hover:border-neutral-300/80 transition-colors"
            >
              <div className="w-10 h-10 bg-[#DF5D01] text-white geist-semibold text-xs flex items-center justify-center flex-shrink-0 rounded-lg shadow-sm">
                {item.step}
              </div>
              <div>
                <p className="geist-semibold text-sm text-[#171717] mb-2 tracking-tight">
                  {item.title}
                </p>
                <p className="geist-medium text-sm text-[#525252] leading-relaxed">
                  {item.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      );

    case "checkGrid":
      return (
        <motion.div variants={stagger} className="grid sm:grid-cols-2 gap-4">
          {block.items.map((row, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              className={`flex items-start gap-3 p-4 rounded-xl border ${
                row.applies
                  ? "border-[#e5e5e5] bg-white shadow-sm"
                  : "border-[#e5e5e5] bg-[#fafafa]"
              }`}
            >
              <span
                className={`text-lg flex-shrink-0 ${row.applies ? "text-[#22c55e]" : "text-[#d4d4d4]"}`}
              >
                {row.applies ? "✓" : "-"}
              </span>
              <div>
                <p className="geist-semibold text-sm text-[#171717] tracking-tight">
                  {row.label}
                </p>
                <p className="geist-medium text-xs text-[#737373] mt-0.5">
                  {row.note}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      );

    case "errorCards":
      return (
        <motion.div variants={stagger} className="space-y-4">
          {block.items.map((err, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              className="border border-[#e5e5e5] bg-white rounded-xl p-5 sm:p-6 shadow-sm"
            >
              <div className="flex items-start gap-4">
                <span className="geist-semibold text-xs text-white bg-[#ef4444] px-2 py-1 flex-shrink-0 rounded">
                  {err.code}
                </span>
                <div>
                  <p className="geist-semibold text-sm text-[#171717] mb-1 tracking-tight">
                    {err.title}
                  </p>
                  <p className="geist-medium text-sm text-[#525252] leading-relaxed">
                    {err.fix}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      );

    default:
      return null;
  }
}

// ─── Not found ──────────────────────────────────────────────────────────────
function ArticleNotFound() {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <p className="geist-semibold text-2xl text-[#171717] mb-3">
          Article not found
        </p>
        <p className="geist-medium text-sm text-[#525252] mb-8">
          This article may have moved. Browse the latest from the ABSS blog.
        </p>
        <Link
          href="/blog"
          className="inline-flex items-center justify-center px-6 py-3 bg-[#171717] text-white geist-semibold text-sm rounded-lg hover:bg-black transition-colors"
        >
          Back to Blog
        </Link>
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────
const BlogDetailPage = () => {
  const params = useParams();
  const post = getPost(params?.slug);

  if (!post) return <ArticleNotFound />;

  const related = getRelated(post.related);

  return (
    <div className="overflow-x-hidden w-full min-h-screen bg-white">
      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section className="relative w-full bg-white border-b border-[#E5E5E5] overflow-hidden">
        <div className="absolute inset-0 z-0 select-none pointer-events-none">
          <div className="absolute top-[-25%] left-[-15%] w-[60%] h-[80%] rounded-full bg-gradient-to-br from-orange-200/40 to-amber-100/10 blur-[130px] animate-aurora-pulse" />
          <div className="absolute bottom-[-15%] right-[-10%] w-[55%] h-[75%] rounded-full bg-gradient-to-tr from-blue-200/30 to-indigo-100/10 blur-[130px] animate-aurora-pulse [animation-delay:3s]" />
          <div className="absolute inset-0 bg-grid-mesh opacity-80" />
        </div>

        <div
          className={`relative z-10 max-w-[1160px] mx-auto pt-28 sm:pt-32 pb-10 sm:pb-12 ${sectionPadding}`}
        >
          <motion.div initial="hidden" animate="show" variants={stagger}>
            {/* Breadcrumb */}
            <motion.div
              variants={fadeUp}
              className="flex items-center gap-2 geist-medium text-sm text-[#737373] mb-8"
            >
              <Link href="/blog" className="hover:text-[#171717] transition-colors">
                Blog
              </Link>
              <span>/</span>
              <span className="text-[#525252]">{post.category}</span>
            </motion.div>

            {/* Category + read time */}
            <motion.div
              variants={fadeUp}
              className="flex flex-wrap items-center gap-3 mb-5 sm:mb-6"
            >
              <span className="inline-flex items-center gap-1.5 border border-orange-100 rounded-full px-2.5 py-1 text-[11px] geist-semibold bg-orange-50/40 text-orange-700 shadow-sm">
                {post.category}
              </span>
              <span className="geist-medium text-xs text-[#737373]">
                {post.readTime}
              </span>
              <span className="geist-medium text-xs text-[#737373]">·</span>
              <span className="geist-medium text-xs text-[#737373]">
                {post.date}
              </span>
            </motion.div>

            {/* Title */}
            <motion.h1
              variants={fadeUp}
              className="text-[28px] sm:text-[38px] md:text-[44px] leading-[1.12] text-[#171717] mb-5 sm:mb-6 max-w-[860px] sentient-bold tracking-tight"
            >
              {post.title}
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="text-[15px] sm:text-lg md:text-[19px] font-medium leading-relaxed text-[#262626] mb-8 sm:mb-10 max-w-[760px] geist-medium"
            >
              {post.excerpt}
            </motion.p>


          </motion.div>
        </div>
      </section>

      {/* ── HERO IMAGE ───────────────────────────────────────────── */}
      <section className={`max-w-[1160px] mx-auto ${sectionPadding} mt-10 sm:mt-14 mb-10 sm:mb-14`}>
        <motion.div
          variants={fadeScale}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="overflow-hidden rounded-2xl sm:rounded-3xl bg-[#FAFAFA] border border-[#E5E5E5]/80 relative shadow-sm"
        >
          <img
            src={post.image || "/images/team-photo.jpg"}
            alt={post.title}
            className="w-full h-auto"
          />
          {post.heroBadge && (
            <div className="absolute inset-0 flex items-end p-6 sm:p-8">
              <div className="bg-[#DF5D01] text-white px-4 py-2 rounded-lg geist-semibold text-sm shadow-md">
                {post.heroBadge}
              </div>
            </div>
          )}
        </motion.div>
      </section>

      {/* ── ARTICLE BODY ─────────────────────────────────────────── */}
      <section className={`max-w-[800px] mx-auto ${sectionPadding} pb-12 sm:pb-16`}>
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          variants={stagger}
          className="space-y-8 sm:space-y-10"
        >
          {/* Intro */}
          <motion.div variants={stagger} className="space-y-5">
            {post.intro.map((para, i) => (
              <motion.p
                key={i}
                variants={fadeUp}
                className="text-[#333333] text-[14px] sm:text-[15px] md:text-[16px] leading-[1.85] geist-medium"
              >
                {para}
              </motion.p>
            ))}
          </motion.div>

          {/* TL;DR */}
          {post.tldr?.length > 0 && (
            <Block block={{ type: "tldr", items: post.tldr }} />
          )}

          {/* Sections */}
          {post.sections.map((block, i) => (
            <Block key={i} block={block} />
          ))}

          {/* CTA */}
          <motion.div variants={fadeUp}>
            <CtaBanner
              badge={post.cta.badge}
              title={post.cta.title}
              description={post.cta.description}
              primary={post.cta.primary}
              secondary={post.cta.secondary}
              tertiary={null}
            />
          </motion.div>
        </motion.div>
      </section>

      {/* ── RELATED ARTICLES ─────────────────────────────────────── */}
      {related.length > 0 && (
        <section className="w-full bg-[#f5f5f5] border-t border-[#e5e5e5]">
          <div
            className={`max-w-[1160px] mx-auto ${sectionPadding} py-12 sm:py-16 md:py-20`}
          >
            <motion.div className="mb-10 sm:mb-14" variants={stagger} {...viewportOnce}>
              <motion.p
                variants={fadeUp}
                className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-3"
              >
                Keep Reading
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="text-[22px] sm:text-[32px] md:text-[38px] leading-[1.2] text-[#171717] sentient-medium tracking-tight"
              >
                Related Articles
              </motion.h2>
            </motion.div>

            <motion.div
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
              variants={stagger}
              {...viewportOnce}
            >
              {related.map((article) => (
                <motion.div key={article.slug} variants={cardItem}>
                  <Link
                    href={`/blog-details/${article.slug}`}
                    className="flex flex-col bg-white border border-[#E5E5E5]/80 rounded-2xl p-6 sm:p-7 group hover:border-[#df5d01]/30 hover:shadow-lg transition-all duration-300 shadow-sm h-full"
                  >
                    <span className="inline-flex items-center gap-1.5 border border-[#e5e5e5] rounded-full px-2 py-0.5 text-[11px] geist-semibold bg-[#FAFAFA] text-[#525252] shadow-sm mb-4 self-start">
                      {article.category}
                    </span>
                    <h3 className="text-[15px] sm:text-base font-semibold text-[#171717] geist-semibold tracking-tight leading-relaxed mb-4 flex-1 group-hover:text-black transition-colors">
                      {article.title}
                    </h3>
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-[#e5e5e5]">
                      <span className="geist-medium text-xs text-[#737373]">
                        {article.date} · {article.readTime}
                      </span>
                      <span className="geist-semibold text-xs text-[#DF5D01]">
                        Read →
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetailPage;
